import { useContext, useEffect, useRef, useState } from 'react';
import {
    Box, Stack, Typography, IconButton, Chip, TextField,
    CircularProgress, Alert, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTicketSocket } from '../../hooks/useTicketSocket';
import { TranslationContext } from '../../Context/TranslationContext';
import { useTranslation } from '../../hooks/useTranslation';
import { LOCALE_BY_LANG, formatFileSize } from '../../Utils/i18nHelpers';
import {
    getTicket, listTicketMessages, addTicketMessage, uploadTicketAttachments, attachmentUrl
} from '../../Utils/ticketApi';

const STATUS_COLORS = {
    open: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'default',
};

const URL_SPLIT_REGEX = /(https?:\/\/[^\s]+)/g;
const URL_TEST_REGEX = /^https?:\/\/[^\s]+$/;

function Linkified({ text }) {
    const parts = String(text).split(URL_SPLIT_REGEX);
    return (
        <>
            {parts.map((part, i) =>
                URL_TEST_REGEX.test(part)
                    ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: 'inherit', wordBreak: 'break-all' }}>{part}</a>
                    : <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>
            )}
        </>
    );
}

function AttachmentPreview({ attachment }) {
    const t = useTranslation('Tickets.json');
    const url = attachmentUrl(attachment.url);
    if (attachment.type?.startsWith('image/')) {
        return (
            <Box component="a" href={url} target="_blank" rel="noreferrer" sx={{ display: 'block', mt: 1 }}>
                <Box component="img" src={url} alt={attachment.name}
                     sx={{ maxWidth: 260, maxHeight: 200, borderRadius: 2, display: 'block' }} />
            </Box>
        );
    }
    if (attachment.type?.startsWith('video/')) {
        return (
            <Box component="video" src={url} controls
                 sx={{ maxWidth: 280, maxHeight: 220, borderRadius: 2, display: 'block', mt: 1 }} />
        );
    }
    return (
        <Chip
            component="a" href={url} target="_blank" rel="noreferrer" clickable
            icon={<InsertDriveFileOutlinedIcon />}
            label={`${attachment.name}${attachment.size ? ` · ${formatFileSize(attachment.size, t)}` : ''}`}
            sx={{ mt: 1, maxWidth: 280 }}
        />
    );
}

function MessageBubble({ message, isOwn }) {
    const t = useTranslation('Tickets.json');
    const { currentLanguage } = useContext(TranslationContext);
    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;
    const author = [message.author_firstname, message.author_lastname].filter(Boolean).join(' ') ||
        (message.author_type === 'admin' ? t('tickets.support_author') : t('tickets.you'));

    return (
        <Stack direction="row" justifyContent={isOwn ? 'flex-end' : 'flex-start'} sx={{ px: 1 }}>
            <Box sx={{ maxWidth: '75%' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, textAlign: isOwn ? 'right' : 'left' }}>
                    {author} · {new Date(message.created_at).toLocaleString(locale)}
                </Typography>
                <Box
                    sx={{
                        bgcolor: isOwn ? 'primary.main' : 'action.hover',
                        color: isOwn ? '#fff' : 'text.primary',
                        borderRadius: 3,
                        px: 2, py: 1.25,
                    }}
                >
                    {message.content && <Linkified text={message.content} />}
                    {(message.attachments || []).map((att, i) => (
                        <AttachmentPreview key={i} attachment={att} />
                    ))}
                </Box>
            </Box>
        </Stack>
    );
}

export default function TicketConversation({ ticketId, onBack }) {
    const t = useTranslation('Tickets.json');
    const tCommon = useTranslation('Common.json');
    const formatSize = (bytes) => formatFileSize(bytes, t);
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [content, setContent] = useState('');
    const [stagedFiles, setStagedFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);
    const listEndRef = useRef(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [ticketData, msgs] = await Promise.all([getTicket(ticketId), listTicketMessages(ticketId)]);
            setTicket(ticketData);
            setMessages(msgs);
        } catch (e) {
            setError(tCommon('common.error.generic'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    useEffect(() => {
        listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useTicketSocket({
        onMessageCreated: (msg) => {
            if (msg.ticket_id !== ticketId) return;
            setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
        }
    });

    const handleFilesSelected = (e) => {
        const files = Array.from(e.target.files || []);
        setStagedFiles(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeStagedFile = (index) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        if (!content.trim() && stagedFiles.length === 0) return;
        setSending(true);
        try {
            let attachments = [];
            if (stagedFiles.length > 0) {
                attachments = await uploadTicketAttachments(ticketId, stagedFiles);
            }
            const msg = await addTicketMessage(ticketId, content.trim() || t('tickets.attachment_placeholder'), attachments);
            setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
            setContent('');
            setStagedFiles([]);
        } catch (e) {
            setError(tCommon('common.error.generic'));
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 500 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600 }}>{ticket?.title}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        {ticket?.status && (
                            <Chip size="small" label={t(`tickets.status.${ticket.status}`)} color={STATUS_COLORS[ticket.status] || 'default'} />
                        )}
                        {ticket?.priority && (
                            <Chip size="small" variant="outlined" label={t(`tickets.priority.${ticket.priority}`)} />
                        )}
                        {ticket?.application && (
                            <Chip size="small" variant="outlined" label={ticket.application} />
                        )}
                        {ticket?.request_type && (
                            <Chip size="small" variant="outlined" label={t(`tickets.request_type.${ticket.request_type}`)} />
                        )}
                    </Stack>
                    {ticket?.page_url && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }} noWrap>
                            {t('tickets.page_concerned')}<a href={ticket.page_url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{ticket.page_url}</a>
                        </Typography>
                    )}
                </Box>
                <Tooltip title={t('tickets.refresh')}>
                    <IconButton onClick={load}><RefreshIcon /></IconButton>
                </Tooltip>
            </Stack>

            {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
                <Stack spacing={2}>
                    {ticket?.description && (
                        <MessageBubble
                            message={{ content: ticket.description, author_type: 'client', author_firstname: '', author_lastname: '', created_at: ticket.created_at, attachments: [] }}
                            isOwn={true}
                        />
                    )}
                    {messages.map(msg => (
                        <MessageBubble key={msg.id} message={msg} isOwn={msg.author_type !== 'admin'} />
                    ))}
                    <div ref={listEndRef} />
                </Stack>
            </Box>

            {stagedFiles.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1, flexWrap: 'wrap', gap: 1 }}>
                    {stagedFiles.map((file, i) => (
                        <Chip
                            key={i}
                            label={`${file.name} · ${formatSize(file.size)}`}
                            onDelete={() => removeStagedFile(i)}
                            deleteIcon={<CloseIcon />}
                            size="small"
                        />
                    ))}
                </Stack>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesSelected} />
                <IconButton onClick={() => fileInputRef.current?.click()} disabled={sending}>
                    <AttachFileIcon />
                </IconButton>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder={t('tickets.message_placeholder')}
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={sending}
                />
                <IconButton
                    onClick={handleSend}
                    disabled={sending || (!content.trim() && stagedFiles.length === 0)}
                    sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
                >
                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                </IconButton>
            </Stack>
        </Box>
    );
}
